import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";

export interface EntitlementsResult {
    activeModules: string[];
    hasPaidModule: boolean;
    customerId: string | null;
}

/**
 * Query key includes current_clinic_id so that when profile loads/updates after
 * first login, entitlements refetch and navigation shows correct links.
 */
export function useEntitlements() {
    const { user } = useAuth();
    const { data: profile } = useProfile();

    return useQuery<EntitlementsResult>({
        queryKey: ["entitlements", profile?.current_clinic_id ?? null],
        queryFn: async () => {
            const currentClinicId = profile?.current_clinic_id ?? null;
            if (!currentClinicId) {
                return { activeModules: [], hasPaidModule: false, customerId: null };
            }

            const { data: clinic, error: clinicError } = await supabase
                .from("clinics")
                .select("customer_id")
                .eq("id", currentClinicId)
                .single();

            if (clinicError) throw clinicError;
            const customerId = clinic?.customer_id as string | null;
            if (!customerId) {
                return { activeModules: [], hasPaidModule: false, customerId: null };
            }

            const { data: rows, error: modulesError } = await supabase
                .from("customer_modules")
                .select(`
                    module_id,
                    is_active,
                    modules!inner (
                        id,
                        value,
                        is_paid
                    )
                `)
                .eq("customer_id", customerId)
                .eq("is_active", true);

            if (modulesError) throw modulesError;

            const activeModules = (rows || []).map((r: any) => r.modules.value as string);
            const hasPaidModule = (rows || []).some((r: any) => !!r.modules?.is_paid);

            return { activeModules, hasPaidModule, customerId };
        },
        enabled: !!user && profile !== undefined && !!profile?.current_clinic_id,
        staleTime: 30_000,
    });
}



